import { useState, type FormEvent, type SyntheticEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";

import toast from "components/partials/toast/toast";

import { useCurrentClient } from "hooks/api/use-clients";
import {
  useGoogleFontsQuery,
  useSaveThemeSections,
  useThemeQuery,
  typekitKitQueryOptions,
  type SavedTheme,
  type ThemeSectionsUpdate,
} from "hooks/api/use-theme";
import { useUnsavedChangesGuard } from "hooks/use-unsaved-changes-guard";
import { useUnsavedChangesNavigation } from "contexts/unsaved-changes-navigation-context";

import {
  colorsFormValuesToPalette,
  paletteToColorsFormValues,
} from "containers/admin/clients/client/theme-configuration-page/brand-identity/colors-tab/colors-tab.helpers";
import {
  fontsToFormValues,
  toSavedFonts,
} from "containers/admin/clients/client/theme-configuration-page/brand-identity/fonts-tab/fonts-tab.helpers";
import { useAdobeProjectId } from "containers/admin/clients/client/theme-configuration-page/brand-identity/fonts-tab/use-adobe-project-id";

import type { GoogleFont } from "containers/admin/clients/client/theme-page/preview/sub-tabs/fonts/google-fonts/google-fonts.types";
import type { BeeFontData } from "containers/admin/clients/client/theme-page/preview/preview.types";
import type { TypekitFamily } from "types";
import type {
  BrandIdentityFormValues,
  BrandIdentityTab,
} from "containers/admin/clients/client/theme-configuration-page/brand-identity/brand-identity.types";

import { DEFAULT_COLORS_FORM_VALUES } from "containers/admin/clients/client/theme-configuration-page/brand-identity/colors-tab/colors-tab.constants";

const toFormValues = ({ palette, fonts }: SavedTheme): BrandIdentityFormValues => ({
  colors: paletteToColorsFormValues(palette),
  fonts: fontsToFormValues(fonts),
});

type FontSources = {
  googleFonts: GoogleFont[];
  adobeFamilies: TypekitFamily[] | undefined;
  previousBeeFonts: BeeFontData[] | undefined;
};

const toSectionsUpdate = (
  tab: BrandIdentityTab,
  values: BrandIdentityFormValues,
  { googleFonts, adobeFamilies, previousBeeFonts }: FontSources,
): ThemeSectionsUpdate =>
  tab === "colors"
    ? { palette: colorsFormValuesToPalette(values.colors) }
    : { fonts: toSavedFonts(values.fonts, googleFonts, adobeFamilies, previousBeeFonts) };

export const useBrandIdentity = () => {
  const client = useCurrentClient();
  const { data: theme, isLoading } = useThemeQuery(client.id);
  const saveThemeSections = useSaveThemeSections(client.id);
  const queryClient = useQueryClient();
  const { requestNavigation } = useUnsavedChangesNavigation();

  const [activeTab, setActiveTab] = useState<BrandIdentityTab>("colors");
  const [isEditing, setIsEditing] = useState(false);

  const form = useForm<BrandIdentityFormValues>({
    mode: "onChange",
    defaultValues: { colors: DEFAULT_COLORS_FORM_VALUES, fonts: fontsToFormValues() },
    values: theme ? toFormValues(theme) : undefined,
    // A background refetch of the theme must not wipe what is being edited.
    resetOptions: { keepDirtyValues: true },
  });

  const { data: googleFonts = [] } = useGoogleFontsQuery();
  const { applyDraft: applyAdobeDraft } = useAdobeProjectId(form);

  const discardChanges = () => {
    // `reset()` inherits `resetOptions`, so the dirty values a refetch must keep are dropped explicitly here.
    form.reset(undefined, { keepDirtyValues: false });
    setIsEditing(false);
  };

  // Only the active tab is ever editable, so the form's dirty state is that tab's.
  const { handleCancelClick, unsavedChangesDialog } = useUnsavedChangesGuard({
    isEditing,
    isDirty: form.formState.isDirty,
    onDiscard: discardChanges,
  });

  const handleTabChange = (_event: SyntheticEvent, tab: BrandIdentityTab) => {
    if (!isEditing) {
      setActiveTab(tab);

      return;
    }

    // Routed through the navigation guard so a dirty tab asks before its edits are dropped.
    requestNavigation({
      run: () => {
        discardChanges();
        setActiveTab(tab);
      },
    });
  };

  const handleEdit = () => setIsEditing(true);

  const saveValues = async (values: BrandIdentityFormValues) => {
    try {
      await saveThemeSections.mutateAsync(
        toSectionsUpdate(activeTab, values, {
          googleFonts,
          // Read from the cache at save time: a Project ID applied by this very Save is not in any render yet.
          adobeFamilies: queryClient.getQueryData(
            typekitKitQueryOptions(client.id, values.fonts.adobeFontsProjectId).queryKey,
          )?.kit.families,
          previousBeeFonts: theme?.fonts?.beeFonts,
        }),
      );
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to save theme:", error);
      toast.error({ data: { title: "Failed to save theme" } });
    }
  };

  const submit = async () => {
    if (activeTab === "fonts" && !(await applyAdobeDraft())) {
      return;
    }

    await form.handleSubmit(saveValues)();
  };

  const handleSave = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void submit();
  };

  return {
    form,
    activeTab,
    isEditing,
    isLoading,
    isSaving: saveThemeSections.isPending,
    unsavedChangesDialog,
    handleTabChange,
    handleEdit,
    handleCancel: handleCancelClick,
    handleSave,
  };
};
